import { z } from 'zod';

import { BookingGranularitySchema } from './distribution.js';

/**
 * Registered hours & deviation contracts (M7 — PM-2044..2049).
 *
 * Registered hours arrive as SIGNED DELTAS from the source system (TimeLog);
 * the summary is a maintained running total, never recomputed from bookings.
 * `POST /api/rp/registered-hours/deltas` is the internal inbound endpoint
 * until the real integration lands in M12.
 */

const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Expected an ISO date (YYYY-MM-DD)');

export const RegisteredHoursDeltaEventSchema = z.object({
  /** Source-system identity — the PM-2045 idempotency key. */
  externalEventId: z.string().min(1),
  allocationId: z.string().uuid(),
  /** Signed: negative deltas are corrections/deletions in TimeLog. */
  deltaHours: z.number().min(-9999.99).max(9999.99).multipleOf(0.01),
  registrationDate: isoDate,
});
export type RegisteredHoursDeltaEvent = z.infer<typeof RegisteredHoursDeltaEventSchema>;

export const ApplyRegisteredHoursDeltasRequestSchema = z.object({
  events: z.array(RegisteredHoursDeltaEventSchema).min(1).max(500),
});
export type ApplyRegisteredHoursDeltasRequest = z.infer<
  typeof ApplyRegisteredHoursDeltasRequestSchema
>;

/** PM-2049 rejection reasons — the reconciliation queue's `reason` column. */
export const RECONCILIATION_REASONS = [
  'ALLOCATION_NOT_FOUND',
  'NOT_LEAF_TASK',
  'NEGATIVE_TOTAL',
] as const;
export type ReconciliationReason = (typeof RECONCILIATION_REASONS)[number];

export const RegisteredHoursDeltaOutcomeSchema = z.object({
  externalEventId: z.string(),
  outcome: z.enum(['applied', 'duplicate', 'rejected']),
  reason: z.enum(RECONCILIATION_REASONS).optional(),
});
export type RegisteredHoursDeltaOutcome = z.infer<typeof RegisteredHoursDeltaOutcomeSchema>;

export const ApplyRegisteredHoursDeltasResponseSchema = z.object({
  results: z.array(RegisteredHoursDeltaOutcomeSchema),
});
export type ApplyRegisteredHoursDeltasResponse = z.infer<
  typeof ApplyRegisteredHoursDeltasResponseSchema
>;

export const ReconciliationQueueEntrySchema = z.object({
  id: z.number().int(),
  reason: z.string(),
  state: z.enum(['pending', 'resolved', 'discarded']),
  payload: RegisteredHoursDeltaEventSchema.nullable(),
  receivedAt: z.string(),
});
export type ReconciliationQueueEntry = z.infer<typeof ReconciliationQueueEntrySchema>;

export const ReconciliationQueueResponseSchema = z.object({
  entries: z.array(ReconciliationQueueEntrySchema),
});
export type ReconciliationQueueResponse = z.infer<typeof ReconciliationQueueResponseSchema>;

/** PM-2044 done-when: recompute-and-compare. Should always come back empty. */
export const RegisteredHoursVerifyResponseSchema = z.object({
  mismatches: z.array(
    z.object({
      allocationId: z.string().uuid(),
      summaryHours: z.number(),
      deltaSum: z.number(),
    }),
  ),
});
export type RegisteredHoursVerifyResponse = z.infer<typeof RegisteredHoursVerifyResponseSchema>;

/** PM-2048: bulk deviation distribution over a subtree. */
export const DistributeDeviationRequestSchema = z.object({
  /** Must resolve to an ADDITIVE registry entry (DL-019). */
  strategy: z.string().min(1).default('PROPORTIONAL_ADD'),
  granularity: BookingGranularitySchema.default(0.25),
});
export type DistributeDeviationRequest = z.infer<typeof DistributeDeviationRequestSchema>;

export const DISTRIBUTE_DEVIATION_SKIP_REASONS = ['NO_RESOURCE', 'ZERO_CAPACITY'] as const;

export const DistributeDeviationResponseSchema = z.object({
  processed: z.number().int(),
  totalHoursDistributed: z.number(),
  skipped: z.array(
    z.object({
      allocationId: z.string().uuid(),
      reason: z.enum(DISTRIBUTE_DEVIATION_SKIP_REASONS),
    }),
  ),
});
export type DistributeDeviationResponse = z.infer<typeof DistributeDeviationResponseSchema>;
